import type { Icon } from "@phosphor-icons/react";
import { CellSignalFull, CircleHalf, Tag, Trash, UserPlus, X } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import type { ItemLite } from "@plannello/shared";
import { Button as AriaButton, Toolbar } from "react-aria-components";
import { Kbd } from "@/components/ui";
import { deleteItems } from "@/data/mutations";
import { cx } from "@/lib/cx";
import { openPicker } from "./PickerHost";
import type { PickerKind } from "./pickerModels";

/**
 * Bottom-floating bar while items are selected. Each button opens the same picker as the
 * keyboard shortcut, applied to every selected item with one undo.
 */
export function SelectionBar({
  count,
  total,
  getTargets,
  onClear,
}: {
  count: number;
  total: number;
  getTargets: () => ItemLite[];
  onClear: () => void;
}) {
  const action = (kind: PickerKind) => (e: { target: Element }) =>
    openPicker(kind, getTargets(), e.target as HTMLElement);
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 flex justify-center px-4">
      <Toolbar
        aria-label={m.selection_label()}
        className="anim-toast pointer-events-auto flex max-w-full items-center gap-1 overflow-x-auto rounded-panel border border-popover-border bg-raised p-1 shadow-lg"
      >
        <span className="flex h-(--control-h-md) items-center px-2 text-sm font-medium text-fg tnum" aria-live="polite">
          {m.selection_count({ count })}
          <span className="ml-1 text-fg-subtle">/ {total}</span>
        </span>
        <span aria-hidden="true" className="mx-0.5 h-4 w-px bg-line" />
        <BarButton icon={CircleHalf} label={m.prop_status()} shortcut="S" onPress={action("status")} />
        <BarButton icon={CellSignalFull} label={m.prop_priority()} shortcut="P" onPress={action("priority")} />
        <BarButton icon={UserPlus} label={m.prop_assignees()} shortcut="A" onPress={action("assignee")} />
        <BarButton icon={Tag} label={m.prop_labels()} shortcut="L" onPress={action("labels")} />
        <BarButton icon={Trash} label={m.cmd_delete()} onPress={() => void deleteItems(getTargets())} />
        <span aria-hidden="true" className="mx-0.5 h-4 w-px bg-line" />
        <AriaButton
          aria-label={m.selection_clear()}
          onPress={onClear}
          className="press transition-ui flex size-(--control-h-md) items-center justify-center rounded-control text-fg-muted data-hovered:bg-hover data-hovered:text-fg"
        >
          <X size={14} aria-hidden="true" />
        </AriaButton>
      </Toolbar>
    </div>
  );
}

function BarButton({
  icon: I,
  label,
  shortcut,
  onPress,
}: {
  icon: Icon;
  label: string;
  shortcut?: string;
  onPress: (e: { target: Element }) => void;
}) {
  return (
    <AriaButton
      onPress={onPress}
      className={cx(
        "press transition-ui flex h-(--control-h-md) shrink-0 items-center gap-1.5 rounded-control px-2 text-sm text-fg-muted",
        "data-hovered:bg-hover data-hovered:text-fg data-pressed:bg-active",
      )}
    >
      <I size={14} aria-hidden="true" />
      <span className="whitespace-nowrap">{label}</span>
      {shortcut ? <Kbd keys={[shortcut]} className="ml-0.5" /> : null}
    </AriaButton>
  );
}
